import moment from "moment";
import React, {
  ComponentType,
  MouseEvent,
  ReactNode,
  SyntheticEvent,
  useState,
} from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import OutlinedInput from "@mui/material/OutlinedInput";

import AutocompleteSelectInput, {
  AutocompleteSelectInputProps,
} from "veneer/AutocompleteSelectInput";
import Box from "veneer/Box";
import Checkbox from "veneer/Checkbox";
import VeneerChip from "veneer/Chip";
import DateTimeRangePicker, {
  DateTimeRangePickerPreset,
  TimeRange,
} from "veneer/DateTimeRangePicker";
import Divider from "veneer/Divider";
import { IconButton } from "veneer/IconButton";
import InputLabel from "veneer/InputLabel";
import ListItem from "veneer/ListItem";
import ListItemButton from "veneer/ListItemButton";
import ListItemText from "veneer/ListItemText";
import Paper from "veneer/Paper";
import Popover from "veneer/Popover";
import Stack from "veneer/Stack";
import { CloseIcon } from "veneer/SvgIcon";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";

interface DataTableMultiSelectFilterProps<T> {
  dataTestId: string;
  groupBy?: AutocompleteSelectInputProps<T, true>["groupBy"];
  isOptionEqualToValue?: (option: T, selected: T) => boolean;
  label: string;
  onChange: (selected: ReadonlyArray<T>) => void;
  onInteractionChange?: (isInteracting: boolean) => void;
  options: ReadonlyArray<T>;
  renderGroup?: AutocompleteSelectInputProps<T, true>["renderGroup"];
  renderOption?: AutocompleteSelectInputProps<T, true>["renderOption"];
  value: ReadonlyArray<T>;
}

interface DataTableDateRangeFilterProps {
  dataTestId: string;
  disableClearable?: boolean;
  label: string;
  multiInput?: boolean;
  onChange: (range: TimeRange) => void;
  onInteractionChange?: (isInteracting: boolean) => void;
  onPresetChange?: (
    range: TimeRange,
    preset: DateTimeRangePickerPreset,
  ) => void;
  presets?: ReadonlyArray<DateTimeRangePickerPreset>;
  selectedPresetId?: string | null;
  value: TimeRange;
}

interface DataTableFilterContainerProps {
  children: ReactNode;
}

export const DATA_TABLE_DATE_RANGE_PRESETS: ReadonlyArray<DateTimeRangePickerPreset> =
  [
    {
      id: "lastHour",
      label: "Last Hour",
      getValue: () => [moment().subtract(1, "hour"), moment()],
    },
    {
      id: "last24Hours",
      label: "Last 24 Hours",
      getValue: () => [moment().subtract(1, "day"), moment()],
    },
    {
      id: "last7Days",
      label: "Last 7 Days",
      getValue: () => [moment().subtract(7, "days"), moment()],
    },
    {
      id: "last30Days",
      label: "Last 30 Days",
      getValue: () => [moment().subtract(30, "days"), moment()],
    },
  ];

const DropdownMenu = styled(Paper)(({ theme }) => ({
  boxShadow: theme.shadows[1],
  maxWidth: 500,
  minWidth: "100%",
  width: "fit-content",
  "& .MuiAutocomplete-option": {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const SelectedCount = styled(VeneerChip)({
  backgroundColor: Colors.blue300,
  color: Colors.blue1000,
});

const MultiSelectAutocomplete = styled(
  AutocompleteSelectInput as ComponentType<any>,
)({
  width: "fit-content",
  "& .MuiAutocomplete-inputRoot": {
    flexWrap: "nowrap",
    minWidth: "175px",
    overflow: "hidden",
  },
  "& .MuiAutocomplete-input": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}) as ComponentType<any>;

const FilterContainer = styled(Stack)({
  ".MuiBox-root:has(> &[data-table-filters='true'])": {
    minWidth: 0,
    width: "100%",
  },
  flexWrap: "wrap",
  minWidth: 0,
});

const DateRangeFilterControl = styled(FormControl)(({ theme }) => ({
  minWidth: 175,
  "& .MuiOutlinedInput-root": {
    cursor: "pointer",
    height: 37,
  },
  "& .MuiInputAdornment-positionEnd": {
    gap: theme.spacing(0.5),
  },
  "& .MuiInputBase-input": {
    width: 125,
  },
  "&:not(.has-selected-range) .MuiOutlinedInput-input::placeholder": {
    color: Colors.gray700,
    opacity: 1,
  },
  "&.has-selected-range .MuiOutlinedInput-input": {
    color: Colors.gray400,
    cursor: "pointer",
    paddingLeft: 0,
  },
  "& .date-range-clear": {
    visibility: "hidden",
  },
  "&:hover .date-range-clear, &:focus-within .date-range-clear": {
    visibility: "visible",
  },
}));

const DateRangeFilterMenu = styled(Box)(({ theme }) => ({
  maxWidth: "calc(100vw - 16px)",
  padding: theme.spacing(1, 0),
  width: 588,
}));

const DateRangePickerContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 1, 2),
}));

export function DataTableFilterContainer({
  children,
}: DataTableFilterContainerProps) {
  return (
    <FilterContainer
      alignItems="flex-start"
      data-table-filters="true"
      direction="row"
      gap={1}
    >
      {children}
    </FilterContainer>
  );
}

const renderSelectedTags = (selected: ReadonlyArray<unknown>) =>
  selected.length > 0 ? (
    <SelectedCount label={selected.length} size="small" />
  ) : null;

const getOptionLabel = (option: unknown): string => {
  if (
    typeof option === "object" &&
    option !== null &&
    "label" in option &&
    typeof option.label === "string"
  ) {
    return option.label;
  }

  return String(option);
};

export function DataTableMultiSelectFilter<T>({
  dataTestId,
  groupBy,
  isOptionEqualToValue,
  label,
  onChange,
  onInteractionChange,
  options,
  renderGroup,
  renderOption,
  value,
}: DataTableMultiSelectFilterProps<T>) {
  const defaultRenderOption: NonNullable<
    AutocompleteSelectInputProps<T, true>["renderOption"]
  > = (props, option, { selected }) => (
    <ListItem {...props}>
      <Box alignItems="center" display="flex">
        <Checkbox checked={selected} size="small" />
        {getOptionLabel(option)}
      </Box>
    </ListItem>
  );

  return (
    <MultiSelectAutocomplete
      dataTestId={dataTestId}
      disableCloseOnSelect
      groupBy={groupBy}
      inputProps={{
        label,
        placeholder: value.length > 0 ? "selected" : undefined,
      }}
      isOptionEqualToValue={isOptionEqualToValue}
      multiple
      onChange={(_event: SyntheticEvent, selected: Array<T> | null) =>
        onChange(selected ?? [])
      }
      onClose={() => onInteractionChange?.(false)}
      onOpen={() => onInteractionChange?.(true)}
      options={[...options]}
      PaperComponent={DropdownMenu}
      renderGroup={renderGroup}
      renderOption={renderOption ?? defaultRenderOption}
      renderTags={renderSelectedTags}
      size="small"
      value={[...value]}
    />
  );
}

export function DataTableDateRangeFilter({
  dataTestId,
  disableClearable,
  label,
  onChange,
  multiInput = true,
  presets = DATA_TABLE_DATE_RANGE_PRESETS,
  onInteractionChange,
  onPresetChange,
  selectedPresetId,
  value,
}: DataTableDateRangeFilterProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const hasSelectedRange = value.some((date) => date !== null);

  const openMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onInteractionChange?.(true);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    onInteractionChange?.(false);
  };

  return (
    <>
      <DateRangeFilterControl
        className={hasSelectedRange ? "has-selected-range" : undefined}
        data-testid={dataTestId}
        size="small"
      >
        {hasSelectedRange && <InputLabel shrink>{label}</InputLabel>}
        <OutlinedInput
          aria-label={label}
          endAdornment={
            <InputAdornment position="end">
              {hasSelectedRange && !disableClearable && (
                <IconButton
                  className="date-range-clear"
                  dataTestId={`${dataTestId}-clear`}
                  icon={<CloseIcon />}
                  onClick={() => {
                    onChange([null, null]);
                    closeMenu();
                  }}
                  size="small"
                  stopPropagation
                  title="Clear"
                />
              )}
              {isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
            </InputAdornment>
          }
          inputProps={{ readOnly: true }}
          label={hasSelectedRange ? label : undefined}
          onClick={openMenu}
          placeholder={hasSelectedRange ? undefined : label}
          startAdornment={
            hasSelectedRange ? (
              <InputAdornment position="start">
                <SelectedCount label={1} size="small" />
              </InputAdornment>
            ) : undefined
          }
          value={hasSelectedRange ? "selected" : ""}
        />
      </DateRangeFilterControl>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        dataTestId={`${dataTestId}-menu`}
        onClose={closeMenu}
        open={isOpen}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
      >
        <DateRangeFilterMenu>
          <DateRangePickerContainer>
            <DateTimeRangePicker
              dataTestId={`${dataTestId}-date-range`}
              label={label}
              multiInput={multiInput}
              onChange={onChange}
              value={value}
            />
          </DateRangePickerContainer>
          <Box px={1}>
            <Divider />
          </Box>
          {presets.map((preset) => (
            <ListItemButton
              dataTestId={`${dataTestId}-preset-${preset.label}`}
              key={preset.label}
              onClick={() => {
                const range = preset.getValue({ isValid: () => true });
                if (onPresetChange) {
                  onPresetChange(range, preset);
                } else {
                  onChange(range);
                }
                closeMenu();
              }}
              selected={
                selectedPresetId != null && preset.id === selectedPresetId
              }
            >
              <ListItemText primary={preset.label} />
            </ListItemButton>
          ))}
        </DateRangeFilterMenu>
      </Popover>
    </>
  );
}
